import React, { useState, useEffect } from "react";
import AuthLayout from "./AuthLayout";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { EmailOutlined, LockOutlined, Visibility, VisibilityOff, Apple } from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useSignupMutation } from "../../services/auth";
import { toast } from "react-toastify";
import { STORAGE_KEYS } from "../../constants";
import { isValidPhoneNumber } from 'libphonenumber-js';
import { isDisallowedNameKey, sanitizeNameInput, validateNameField } from "../../utils/nameValidation";
import { getSignupDraft, setSignupDraft } from "../../utils/authDraft";
import { formatOrSplitPhoneNumber } from "../../utils/profile";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.5 0-1.96.93-1.96 1.89v2.26h3.32l-.53 3.5h-2.8V24C19.62 23.1 24 18.1 24 12.07z" />
    <path d="M15.46 15.56l.53-3.5h-3.32v-2.26c0-.96.47-1.89 1.96-1.89h1.5V5.11s-1.37-.24-2.68-.24c-2.73 0-4.54 1.68-4.54 4.7v2.72H7.08v3.49h3.04V24c.62.1 1.25.15 1.88.15s1.27-.05 1.88-.15v-8.44h3.58z" fill="white" />
  </svg>
);

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as any) || {};
  const draftData = getSignupDraft();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ first_name?: string; last_name?: string; email?: string; phone_number?: string; password?: string; confirm_password?: string }>({});
  const [formDataState, setFormDataState] = useState({
    first_name: sanitizeNameInput(locationState.first_name || draftData.first_name || ""),
    last_name: sanitizeNameInput(locationState.last_name || draftData.last_name || ""),
    email: locationState.email || draftData.email || "",
  });
  const [passwords, setPasswords] = useState({
    password: locationState.password || "",
    confirmPassword: locationState.confirm_password || ""
  });
  const [phone, setPhone] = useState(locationState.phone_number || draftData.phone_number || "");
  const [countryCode, setCountryCode] = useState(locationState.country_code || draftData.country_code || "+971");

  useEffect(() => {
    setSignupDraft({
      first_name: formDataState.first_name,
      last_name: formDataState.last_name,
      email: formDataState.email,
      phone_number: phone,
      country_code: countryCode,
    });
  }, [formDataState, phone, countryCode]);
  const [phoneFormat, setPhoneFormat] = useState("");
  const [signup, { isLoading }] = useSignupMutation();

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((prev) => !prev);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validateEmail = (value: string) => {
    if (!value) return "Email is required";
    if (value.length > 80) return "Email must not exceed 80 characters";
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return undefined;
  };

  const validatePhone = (value: string) => {
    if (!value) return "Phone number is required";
    const phoneWithPlus = value.startsWith('+') ? value : `+${value}`;
    if (!isValidPhoneNumber(phoneWithPlus)) return "Please enter a valid phone number for the selected country";
    return undefined;
  };

  const validatePassword = (value: string) => {
    if (!value) return "Password is required";
    if (value.includes(" ")) return "Password must not contain spaces";
    if (value.length < 8 || value.length > 30) return "Password must be between 8 and 30 characters";
    if (!/[A-Z]/.test(value)) return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(value)) return "Password must contain at least one lowercase letter";
    if (!/[0-9]/.test(value)) return "Password must contain at least one number";
    if (!/[^A-Za-z0-9]/.test(value)) return "Password must contain at least one special character";
    return undefined;
  };

  const validateField = (name: string, value: string, compareValue?: string) => {
    switch (name) {
      case "first-name": return validateNameField("First name", value);
      case "last-name": return validateNameField("Last name", value);
      case "email": return validateEmail(value);
      case "phone-number": return validatePhone(value);
      case "password": return validatePassword(value);
      case "confirm-password":
        if (!value) return "Confirm password is required";
        if (value !== compareValue) return "Passwords do not match";
        return undefined;
      default:
        return undefined;
    }
  };

  const handleNameInputChange = (name: string, value: string) => {
    const sanitized = sanitizeNameInput(value);
    const errorKey = name === "first-name" ? "first_name" : "last_name";
    const fieldLabel = name === "first-name" ? "First name" : "Last name";
    setFormDataState(prev => ({ ...prev, [errorKey]: sanitized }));

    let errorMsg: string | undefined;
    if (value !== sanitized) {
      errorMsg = `${fieldLabel} can only contain letters`;
    } else if (errors[errorKey]) {
      errorMsg = validateField(name, sanitized);
    }
    setErrors((prev) => ({ ...prev, [errorKey]: errorMsg }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "first-name" || name === "last-name") {
      handleNameInputChange(name, value);
      return;
    }

    if (name === "email") setFormDataState(prev => ({ ...prev, email: value.toLowerCase() }));

    let updatedPasswords = passwords;
    if (name === "password") {
      updatedPasswords = { ...passwords, password: value };
      setPasswords(updatedPasswords);
    } else if (name === "confirm-password") {
      updatedPasswords = { ...passwords, confirmPassword: value };
      setPasswords(updatedPasswords);
    }

    const errorKey = name.replace("-", "_") as keyof typeof errors;
    if (errors[errorKey]) {
      const errorMsg = validateField(name, value, name === "confirm-password" ? updatedPasswords.password : undefined);
      setErrors((prev) => ({ ...prev, [errorKey]: errorMsg }));
    }

    // Also revalidate confirm_password if password changes and confirm_password has an error
    if (name === "password" && errors.confirm_password) {
      const confirmError = validateField("confirm-password", updatedPasswords.confirmPassword, updatedPasswords.password);
      setErrors((prev) => ({ ...prev, confirm_password: confirmError }));
    }
  };

  const handleNameKeyDown = (field: "first_name" | "last_name") => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.nativeEvent.isComposing) return;
    if (!isDisallowedNameKey(e.key)) return;
    e.preventDefault();
    const fieldLabel = field === "first_name" ? "First name" : "Last name";
    setErrors((prev) => ({ ...prev, [field]: `${fieldLabel} can only contain letters` }));
  };

  const validateSignupForm = (fields: any) => {
    const fieldMapping = [
      { key: "first_name", fieldName: "first-name" },
      { key: "last_name", fieldName: "last-name" },
      { key: "email", fieldName: "email" },
      { key: "phone_number", fieldName: "phone-number" },
      { key: "password", fieldName: "password" },
      { key: "confirm_password", fieldName: "confirm-password", compareTo: fields.password }
    ];

    const newErrors: any = {};
    let hasError = false;

    for (const { key, fieldName, compareTo } of fieldMapping) {
      const error = validateField(fieldName, fields[key], compareTo);
      if (error) {
        newErrors[key] = error;
        hasError = true;
      }
    }

    return { hasError, newErrors };
  };

  const extractSignupToken = (res: any) => {
    let token = res?.data?.access_token || res?.access_token || res?.data?.access || res?.access || res?.data?.token?.access || res?.token?.access;
    if (!token) {
      token = (typeof res?.data?.token === 'string' ? res?.data?.token : null)
        || (typeof res?.token === 'string' ? res?.token : null);
    }
    return token;
  };

  const parseSignupConflictError = (errData: any, setErrors: any) => {
    const msgs = [];
    if (errData.email) {
      msgs.push("email");
      setErrors((prev: any) => ({ ...prev, email: "This email is already registered." }));
    }
    if (errData.phone_number || errData.phone) {
      msgs.push("phone number");
      setErrors((prev: any) => ({ ...prev, phone_number: "This phone number is already registered." }));
    }
    return msgs.length > 0 ? `This ${msgs.join(" and ")} is already registered.` : null;
  };

  const parseSignupDataError = (errData: any, setErrors: any): string | null => {
    if (typeof errData === 'string') return errData;
    const conflictMsg = parseSignupConflictError(errData, setErrors);
    if (conflictMsg) return conflictMsg;

    if (errData.message) return errData.message;
    if (errData.detail) return errData.detail;
    if (errData.error) {
      return typeof errData.error === 'string' ? errData.error : JSON.stringify(errData.error);
    }
    const keys = Object.keys(errData);
    if (keys.length > 0) {
      const firstError = errData[keys[0]];
      if (Array.isArray(firstError) && typeof firstError[0] === 'string') {
        return `${keys[0]}: ${firstError[0]}`;
      }
      return typeof firstError === 'string' ? firstError : JSON.stringify(errData);
    }
    return null;
  };

  const resolveSignupErrorMessage = (err: any, setErrors: any): string => {
    let errorMsg = "Failed to create account";

    if (err?.data) {
      const dataMsg = parseSignupDataError(err.data, setErrors);
      if (dataMsg) errorMsg = dataMsg;
    } else if (err?.error) {
      errorMsg = typeof err.error === 'string' ? err.error : String(err.error);
    } else if (err?.message) {
      errorMsg = typeof err.message === 'string' ? err.message : String(err.message);
    }

    const lowerError = errorMsg.toLowerCase();
    const isTaken = lowerError.includes("exist") || lowerError.includes("taken") || lowerError.includes("already");

    if (lowerError.includes("email") && isTaken) {
      errorMsg = "This email is already registered.";
      setErrors((prev: any) => ({ ...prev, email: errorMsg }));
    } else if ((lowerError.includes("phone") || lowerError.includes("number")) && isTaken) {
      errorMsg = "This phone number is already registered.";
      setErrors((prev: any) => ({ ...prev, phone_number: errorMsg }));
    }

    return errorMsg;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const first_name = sanitizeNameInput((formData.get("first-name") as string) || "").trim();
    const last_name = sanitizeNameInput((formData.get("last-name") as string) || "").trim();
    const email = (formData.get("email") as string || "").toLowerCase().trim();
    const { country_code, phone_number: national_phone } = formatOrSplitPhoneNumber(phone, countryCode);
    const password = passwords.password;
    const confirm_password = passwords.confirmPassword;

    setErrors({});
    const { hasError, newErrors } = validateSignupForm({ first_name, last_name, email, phone_number: phone, password, confirm_password });

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setSignupDraft({
      first_name,
      last_name,
      email,
      phone_number: phone,
    });

    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    const uniqueSuffix = array[0] % 100000;
    const generatedUsername = `${email.split('@')[0]}_${uniqueSuffix}`;

    const payload: any = {
      username: generatedUsername,
      first_name,
      last_name,
      email,
      phone_number: national_phone || phone,
      password,
    };
    if (country_code) {
      payload.country_code = country_code;
    }

    console.log("Signup Payload:", payload);

    try {
      localStorage.removeItem(STORAGE_KEYS.token);

      const res = await signup(payload).unwrap();
      console.log("Signup API response:", res);

      const token = extractSignupToken(res);
      if (token) {
        localStorage.setItem(STORAGE_KEYS.token, token);
      }

      toast.success("Account created successfully. Please verify your OTP.");
      navigate("/otp", {
        state: {
          signupMethod: "email",
          email,
          first_name,
          last_name,
          phone_number: national_phone || phone,
          country_code: country_code || "",
          password,
          confirm_password,
        }
      });
    } catch (err: any) {
      console.error("Signup API Error:", err);
      const errorMsg = resolveSignupErrorMessage(err, setErrors);
      toast.error(errorMsg);
    }
  };

  return (
    <AuthLayout
      subtitle="Welcome to It's My Wall!"
      title={<React.Fragment>Create an account to start<br />creating <span style={{ color: "#8CE352" }}>Stunning</span> wall art.</React.Fragment>}
    >
      <div className="authBox">
        <h2 style={{ position: "relative" }}>
          <img
            src="/static/images/back_arrow.svg"
            alt="back"
            onClick={() => navigate(-1)}
            className="back_arrow"
            style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", cursor: "pointer", width: 24, height: 24 }}
          />
          Create your account
        </h2>
        <p className="subtitle" style={{ marginBottom: 40 }}>
          Enter your details to get started
        </p>

        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group text_field">
            <label htmlFor="first-name">First Name <span style={{ color: "red" }}>*</span></label>
            <TextField
              id="first-name"
              name="first-name"
              value={formDataState.first_name}
              placeholder="Enter your first name"
              fullWidth
              error={!!errors.first_name}
              helperText={errors.first_name}
              onChange={handleInputChange}
              onKeyDown={handleNameKeyDown("first_name")}
              inputProps={{
                maxLength: 35,
                autoComplete: "given-name",
                inputMode: "text",
              }}
            />
          </div>

          <div className="control_group text_field">
            <label htmlFor="last-name">Last Name <span style={{ color: "red" }}>*</span></label>
            <TextField
              id="last-name"
              name="last-name"
              value={formDataState.last_name}
              placeholder="Enter your last name"
              fullWidth
              error={!!errors.last_name}
              helperText={errors.last_name}
              onChange={handleInputChange}
              onKeyDown={handleNameKeyDown("last_name")}
              inputProps={{
                maxLength: 35,
                autoComplete: "family-name",
                inputMode: "text",
              }}
            />
          </div>

          <div className="control_group text_field">
            <label htmlFor="email">Email Address <span style={{ color: "red" }}>*</span></label>
            <TextField
              id="email"
              name="email"
              type="email"
              value={formDataState.email}
              placeholder="Enter your email address"
              fullWidth
              error={!!errors.email}
              helperText={errors.email}
              onBlur={() => {
                setFormDataState(prev => ({ ...prev, email: prev.email.toLowerCase().trim() }));
              }}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="control_group text_field">
            <label htmlFor="phone-number">Phone Number <span style={{ color: "red" }}>*</span></label>
            <PhoneInput
              country={"ae"}
              value={phone}
              countryCodeEditable={false}
              onChange={(val, data: any) => {
                if (data?.dialCode) {
                  setCountryCode(`+${data.dialCode}`);
                }

                setPhone(val);
                if (data && data.format) {
                  setPhoneFormat(data.format);
                }

                // Real-time validation clear
                if (errors.phone_number) {
                  let errorMsg: string | undefined = undefined;
                  if (!val) errorMsg = "Phone number is required";
                  else {
                    const phoneWithPlus = val.startsWith('+') ? val : `+${val}`;
                    if (!isValidPhoneNumber(phoneWithPlus)) errorMsg = "Please enter a valid phone number for the selected country";
                  }

                  setErrors((prev) => ({ ...prev, phone_number: errorMsg }));
                }
              }}
              placeholder="Enter your phone number"
              enableSearch={true}
              inputStyle={{ width: "100%", ...(errors.phone_number ? { border: "1px solid #d32f2f" } : {}) }}
            />
            {errors.phone_number && (
              <p className="MuiFormHelperText-root Mui-error css-1wc848c-MuiFormHelperText-root" style={{ margin: "3px 14px 0 14px", fontSize: "0.75rem", fontFamily: '"Roboto","Helvetica","Arial",sans-serif', fontWeight: 400, color: "#d32f2f" }}>
                {errors.phone_number}
              </p>
            )}
          </div>

          <div className="control_group text_field">
            <label htmlFor="password">Password <span style={{ color: "red" }}>*</span></label>
            <TextField
              id="password"
              name="password"
              value={passwords.password}
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              fullWidth
              error={!!errors.password}
              helperText={errors.password}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff style={{ color: "rgba(255, 255, 255, 0.4)" }} /> : <Visibility style={{ color: "rgba(255, 255, 255, 0.4)" }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="control_group text_field">
            <label htmlFor="confirm-password">Confirm Password <span style={{ color: "red" }}>*</span></label>
            <TextField
              id="confirm-password"
              name="confirm-password"
              value={passwords.confirmPassword}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              fullWidth
              error={!!errors.confirm_password}
              helperText={errors.confirm_password}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff style={{ color: "rgba(255, 255, 255, 0.4)" }} /> : <Visibility style={{ color: "rgba(255, 255, 255, 0.4)" }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="form_btn" style={{ marginTop: 20 }}>
            <Button
              type="submit"
              className="btn btn_primary w_100"
              disabled={isLoading}
            >
              {isLoading ? "Signing Up..." : "Sign Up"}
            </Button>
          </div>

          <div className="divider">or continue with</div>

          <div className="social_login">
            <Button variant="outlined" startIcon={<GoogleIcon />} onClick={() => window.open("https://accounts.google.com/", "_blank")}>Google</Button>
            <Button variant="outlined" startIcon={<Apple />} onClick={() => window.open("https://appleid.apple.com/", "_blank")}>Apple</Button>
            <Button variant="outlined" startIcon={<FacebookIcon />} onClick={() => window.open("https://www.facebook.com/login.php", "_blank")}>Facebook</Button>
          </div>

          <div className="auth_bottom">
            <p>
              Already have an account?{" "}
              <button
                type="button"
                className="link"
                onClick={() => navigate("/")}
                style={{ marginLeft: 5 }}
              >
                Log In
              </button>
            </p>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Signup;
